import React from 'react';
import { createPortal } from 'react-dom';
import Toast from './Toast';

/**
 * Maximum number of toasts visible at once
 */
const MAX_VISIBLE_TOASTS = 5;

/**
 * ToastContainer renders toasts in a fixed position using React Portal
 * Stacks multiple toasts vertically with proper spacing
 *
 * @param {Object} props - Component props
 * @param {Array} props.toasts - Array of toast objects to render
 * @param {Function} props.onDismiss - Callback when a toast is dismissed
 */
const ToastContainer = ({ toasts, onDismiss }) => {
  // Only show the most recent toasts up to MAX_VISIBLE_TOASTS
  const visibleToasts = toasts.slice(-MAX_VISIBLE_TOASTS);

  // Don't render anything if there are no toasts
  if (visibleToasts.length === 0) {
    return null;
  }

  const containerContent = (
    <div
      aria-label="Notifications"
      aria-live="polite"
      aria-relevant="additions removals"
      className="
        fixed top-4 right-4
        z-50
        flex flex-col gap-3
        pointer-events-none
      "
    >
      {visibleToasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onDismiss={onDismiss}
          />
        </div>
      ))}
    </div>
  );

  // Use portal to render outside the component hierarchy
  // This ensures toasts appear above all other content
  return createPortal(containerContent, document.body);
};

export default ToastContainer;
